import { useEffect, useState } from 'react';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { useDispatchBoard } from '../../lib/dispatch-board.js';

/**
 * KPI strip across the top of the dispatch board — mockup's `.ib-top`.
 *
 * Values reflect the day + view window the dispatcher is currently looking
 * at (not a hardcoded 8AM-4PM "today"). Utilization is computed from real
 * assignment durations and the picked workday hours.
 */
interface KpiStripProps {
  queueCount: number;
  /** Total scheduled hours across all placed cards on the selected day. */
  scheduledHours: number;
  placedCount: number;
  /** Subset of placedCount whose orders carry FOP_COMPLETED_AT. */
  completedCount: number;
  totalOrders: number;
  techCount: number;
  onReset: () => void;
  /** TanStack Query's dataUpdatedAt for the orders query (0 = never). */
  dataUpdatedAt: number;
  isFetching: boolean;
  onRefresh: () => void;
}

export function KpiStrip({
  queueCount,
  scheduledHours,
  placedCount,
  completedCount,
  totalOrders,
  techCount,
  onReset,
  dataUpdatedAt,
  isFetching,
  onRefresh,
}: KpiStripProps) {
  const selectedDate = useDispatchBoard((s) => s.selectedDate);
  const viewHours = useDispatchBoard((s) => s.viewHours);
  // Capacity = active techs × hours in the dispatcher's chosen workday.
  // Doesn't account for breaks, PTO, or overlapping placements — those
  // need the per-tech schedule model (Phase 5). Good enough for a "how
  // booked are we" glance.
  const capacityHours = techCount * viewHours;
  const utilPct = capacityHours === 0 ? 0 : Math.round((scheduledHours / capacityHours) * 100);
  const inProgressCount = Math.max(0, placedCount - completedCount);

  return (
    <div
      className="flex items-center gap-2 flex-wrap px-4 py-2.5 border-b"
      style={{
        borderColor: 'var(--color-border-tertiary)',
        backgroundColor: 'var(--color-background-secondary)',
      }}
    >
      <div className="text-sm font-medium text-ink">Dispatch board</div>
      <div className="text-xs text-ink-muted">/ {friendlySelectedDate(selectedDate)}</div>
      <div className="ml-auto flex items-center gap-1.5 flex-wrap">
        <KpiPill label="In progress" value={inProgressCount} />
        <KpiPill label="Done" value={completedCount} />
        <KpiPill label="Unassigned" value={queueCount} />
        <KpiPill label="Total" value={totalOrders} />
        <KpiPill
          label="Util"
          value={`${utilPct}%`}
          title={`${scheduledHours}h scheduled across ${techCount} tech${techCount === 1 ? '' : 's'} × ${viewHours}h workday`}
        />
        <button
          type="button"
          onClick={onRefresh}
          disabled={isFetching}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-muted hover:bg-surface hover:text-ink disabled:opacity-60"
          aria-label="Refresh the board now"
          title="Refresh the board now (auto-refreshes every 30s)"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
          <UpdatedAgo dataUpdatedAt={dataUpdatedAt} isFetching={isFetching} />
        </button>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-muted hover:bg-surface hover:text-ink"
          aria-label="Unschedule every placement on this day"
          title="Unschedule every placement on this day"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          Clear day
        </button>
      </div>
    </div>
  );
}

/** "updated 12s ago" label that re-renders itself every 10s. */
function UpdatedAgo({ dataUpdatedAt, isFetching }: { dataUpdatedAt: number; isFetching: boolean }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 10_000);
    return () => window.clearInterval(id);
  }, []);
  if (isFetching) return <span>updating…</span>;
  if (!dataUpdatedAt) return <span>refresh</span>;
  const ageSec = Math.max(0, Math.round((Date.now() - dataUpdatedAt) / 1000));
  const label =
    ageSec < 60 ? `${ageSec}s` : `${Math.floor(ageSec / 60)}m${ageSec >= 3600 ? '+' : ''}`;
  return <span>updated {label} ago</span>;
}

function friendlySelectedDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function KpiPill({
  label,
  value,
  title,
}: {
  label: string;
  value: number | string;
  title?: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs bg-surface"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
      title={title}
    >
      {label && <span className="text-ink-muted">{label}</span>}
      <span className="font-medium tabular-nums">{value}</span>
    </span>
  );
}
